import React, { useState, useRef, useCallback } from 'react';
import Icon from '../AppIcon';

const DOCUMENT_STATES = ['Capturada', 'OCR Procesando', 'Pendiente Revisión', 'Validada', 'Contabilizada', 'Pendiente Pago', 'Pagada', 'Archivada'];

const STATE_COLORS = {
  'Capturada': '#60A5FA',
  'OCR Procesando': '#FBBF24',
  'Pendiente Revisión': '#F97316',
  'Validada': '#34D399',
  'Contabilizada': '#A78BFA',
  'Pendiente Pago': '#F87171',
  'Pagada': '#34D399',
  'Archivada': '#6B7280',
};

const PROCESSING_STEPS = [
  { id: 1, label: 'Detectar bordes automáticamente', icon: 'Scan', done: false },
  { id: 2, label: 'Corregir inclinación', icon: 'RotateCcw', done: false },
  { id: 3, label: 'Mejorar contraste', icon: 'Sun', done: false },
  { id: 4, label: 'Limpiar sombras', icon: 'Eraser', done: false },
  { id: 5, label: 'Convertir a PDF', icon: 'FileText', done: false },
  { id: 6, label: 'Guardar imagen original', icon: 'Image', done: false },
  { id: 7, label: 'Guardar PDF procesado', icon: 'Save', done: false },
  { id: 8, label: 'Ejecutar OCR automático', icon: 'Cpu', done: false },
];

const DEPARTMENTS = ['Tienda Física', 'Tienda Online', 'Sala de Corte', 'Distribución', 'Corporativo'];

const emptyOCR = {
  numero_factura: '',
  fecha_factura: '',
  fecha_vencimiento: '',
  proveedor: '',
  cif_proveedor: '',
  base_imponible: '',
  iva: '',
  recargo_equivalencia: '',
  total_factura: '',
  forma_pago: '',
  referencia_proveedor: '',
  observaciones: '',
  departamento: '',
  centro_coste: '',
};

const emptyLine = { producto: '', cantidad: '', precio_compra: '', descuento: '', iva: '', total_linea: '' };

export default function DocumentCapture({ onSave, onClose, moduleLabel = 'Factura', accentColor = '#D6EF4E' }) {
  const [step, setStep] = useState('capture'); // capture | processing | validation | saved
  const [processingSteps, setProcessingSteps] = useState(PROCESSING_STEPS);
  const [processingIdx, setProcessingIdx] = useState(0);
  const [capturedFile, setCapturedFile] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const [ocrData, setOcrData] = useState(emptyOCR);
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [docState, setDocState] = useState('Pendiente Revisión');
  const [dragOver, setDragOver] = useState(false);
  const [multiPages, setMultiPages] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const accent = accentColor;

  const startProcessing = useCallback((file, preview) => {
    setCapturedFile(file);
    setCapturedPreview(preview);
    setStep('processing');
    setProcessingIdx(0);
    const steps = PROCESSING_STEPS?.map(s => ({ ...s, done: false }));
    setProcessingSteps(steps);

    let idx = 0;
    const interval = setInterval(() => {
      setProcessingSteps(prev => prev?.map((s, i) => i <= idx ? { ...s, done: true } : s));
      setProcessingIdx(idx);
      idx++;
      if (idx >= PROCESSING_STEPS?.length) {
        clearInterval(interval);
        setTimeout(() => {
          setOcrData({
            numero_factura: 'FAC-2026-' + Math.floor(Math.random() * 9000 + 1000),
            fecha_factura: new Date()?.toISOString()?.split('T')?.[0],
            fecha_vencimiento: '',
            proveedor: '',
            cif_proveedor: '',
            base_imponible: '',
            iva: '',
            recargo_equivalencia: '',
            total_factura: '',
            forma_pago: '',
            referencia_proveedor: '',
            observaciones: '',
            departamento: '',
            centro_coste: '',
          });
          setStep('validation');
        }, 600);
      }
    }, 380);
  }, []);

  const handleFileSelect = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => startProcessing(file, e?.target?.result);
    reader?.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleMultiPage = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length > 0) {
      setMultiPages(files?.map(f => f?.name));
      handleFileSelect(files?.[0]);
    }
  };

  const updateOCR = (field, value) => setOcrData(prev => ({ ...prev, [field]: value }));
  const updateLine = (idx, field, value) => setLines(prev => prev?.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  const addLine = () => setLines(prev => [...prev, { ...emptyLine }]);
  const removeLine = (idx) => setLines(prev => prev?.filter((_, i) => i !== idx));

  const handleSave = () => {
    onSave?.({ ocrData, lines, docState, file: capturedFile, preview: capturedPreview, multiPages });
    setStep('saved');
  };

  const reset = () => {
    setStep('capture');
    setCapturedFile(null);
    setCapturedPreview(null);
    setOcrData(emptyOCR);
    setLines([{ ...emptyLine }]);
    setMultiPages([]);
    setProcessingSteps(PROCESSING_STEPS);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-4xl max-h-[95vh] overflow-y-auto rounded-2xl flex flex-col"
        style={{ background: 'linear-gradient(135deg, #111111 0%, #0D0D0D 100%)', border: `1px solid ${accent}25` }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}>
              <Icon name="Camera" size={18} style={{ color: accent }} />
            </div>
            <div>
              <h2 className="font-oranienbaum font-bold text-white text-lg">📷 Captura de {moduleLabel}</h2>
              <p className="font-oranienbaum text-white/40 text-xs">Sistema de digitalización documental</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/8 transition-all">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
          {['capture', 'processing', 'validation', 'saved']?.map((s, i) => {
            const labels = ['Captura', 'Procesando', 'Validación', 'Guardado'];
            const active = step === s;
            const done = ['capture', 'processing', 'validation', 'saved']?.indexOf(step) > i;
            return (
              <React.Fragment key={s}>
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: active ? accent : done ? `${accent}40` : 'rgba(255,255,255,0.08)', color: active ? '#000' : done ? accent : 'rgba(255,255,255,0.3)' }}>
                    {done ? '✓' : i + 1}
                  </div>
                  <span className="font-oranienbaum text-xs" style={{ color: active ? accent : 'rgba(255,255,255,0.35)' }}>{labels?.[i]}</span>
                </div>
                {i < 3 && <div className="flex-1 h-px" style={{ background: done ? `${accent}40` : 'rgba(255,255,255,0.07)' }} />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="p-6 flex-1">

          {/* ── STEP: CAPTURE ── */}
          {step === 'capture' && (
            <div className="space-y-5">
              <p className="font-oranienbaum text-white/50 text-sm">Selecciona cómo deseas capturar el documento</p>

              {/* Main capture buttons */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Hacer Foto', sublabel: 'Cámara móvil/tablet', icon: 'Camera', action: () => cameraInputRef?.current?.click() },
                  { label: 'Desde Galería', sublabel: 'Seleccionar imagen', icon: 'Image', action: () => { fileInputRef.current.accept = 'image/*'; fileInputRef?.current?.click(); } },
                  { label: 'Subir PDF', sublabel: 'Arrastrar o seleccionar', icon: 'FileText', action: () => { fileInputRef.current.accept = '.pdf'; fileInputRef?.current?.click(); } },
                  { label: 'Múltiples Páginas', sublabel: 'Escanear varias páginas', icon: 'Files', action: () => document.getElementById('multi-input')?.click() },
                ]?.map((btn, i) => (
                  <button key={i} onClick={btn?.action}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: `${accent}08`, border: `1px solid ${accent}20`, color: accent }}>
                    <Icon name={btn?.icon} size={28} style={{ color: accent }} />
                    <span className="font-oranienbaum font-bold text-sm text-white">{btn?.label}</span>
                    <span className="font-oranienbaum text-xs text-white/40 text-center">{btn?.sublabel}</span>
                  </button>
                ))}
              </div>

              {/* Drag & drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                className="rounded-xl border-2 border-dashed p-8 flex flex-col items-center gap-3 transition-all cursor-pointer"
                style={{ borderColor: dragOver ? accent : 'rgba(255,255,255,0.12)', background: dragOver ? `${accent}08` : 'rgba(255,255,255,0.02)' }}
                onClick={() => fileInputRef?.current?.click()}>
                <Icon name="Upload" size={32} className="text-white/30" />
                <p className="font-oranienbaum text-white/50 text-sm text-center">Arrastra un PDF o imagen aquí, o haz clic para seleccionar</p>
                <p className="font-oranienbaum text-white/25 text-xs">PDF, JPG, PNG, HEIC — Máx. 50MB</p>
              </div>

              {/* Hidden inputs */}
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleFileSelect(e?.target?.files?.[0])} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFileSelect(e?.target?.files?.[0])} />
              <input id="multi-input" type="file" accept="image/*,.pdf" multiple className="hidden" onChange={handleMultiPage} />
            </div>
          )}

          {/* ── STEP: PROCESSING ── */}
          {step === 'processing' && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {capturedPreview && (
                  <div className="w-24 h-32 rounded-xl overflow-hidden border flex-shrink-0" style={{ borderColor: `${accent}25` }}>
                    <img src={capturedPreview} alt="Documento capturado" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-oranienbaum font-bold text-white text-lg mb-1">Procesando documento...</h3>
                  <p className="font-oranienbaum text-white/40 text-sm">El sistema está analizando y optimizando la imagen</p>
                  <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${((processingIdx + 1) / PROCESSING_STEPS?.length) * 100}%`, background: `linear-gradient(90deg, ${accent} 0%, ${accent}80 100%)` }} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {processingSteps?.map((s, i) => (
                  <div key={s?.id} className="flex items-center gap-3 p-3 rounded-xl transition-all"
                    style={{ background: s?.done ? `${accent}08` : 'rgba(255,255,255,0.03)', border: `1px solid ${s?.done ? `${accent}25` : 'rgba(255,255,255,0.06)'}` }}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: s?.done ? `${accent}20` : 'rgba(255,255,255,0.06)' }}>
                      {s?.done
                        ? <Icon name="Check" size={14} style={{ color: accent }} />
                        : i === processingIdx
                          ? <div className="w-3 h-3 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
                          : <Icon name={s?.icon} size={14} className="text-white/25" />}
                    </div>
                    <span className="font-oranienbaum text-sm" style={{ color: s?.done ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.35)' }}>{s?.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP: VALIDATION ── */}
          {step === 'validation' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-oranienbaum font-bold text-white text-lg">Validación de datos OCR</h3>
                  <p className="font-oranienbaum text-white/40 text-sm">Revisa y corrige los datos extraídos antes de guardar</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-oranienbaum text-xs text-white/40">Estado:</span>
                  <select value={docState} onChange={e => setDocState(e?.target?.value)}
                    className="font-oranienbaum text-xs rounded-lg px-3 py-1.5 border outline-none"
                    style={{ background: '#111', color: STATE_COLORS?.[docState] || '#fff', borderColor: `${STATE_COLORS?.[docState] || '#fff'}40` }}>
                    {DOCUMENT_STATES?.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* OCR Header fields */}
              <div className="rounded-xl p-4 space-y-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <h4 className="font-oranienbaum font-bold text-white/70 text-sm uppercase tracking-widest">Cabecera de Factura</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { key: 'numero_factura', label: 'Nº Factura' },
                    { key: 'fecha_factura', label: 'Fecha Factura', type: 'date' },
                    { key: 'fecha_vencimiento', label: 'Fecha Vencimiento', type: 'date' },
                    { key: 'proveedor', label: 'Proveedor' },
                    { key: 'cif_proveedor', label: 'CIF Proveedor' },
                    { key: 'forma_pago', label: 'Forma de Pago' },
                    { key: 'base_imponible', label: 'Base Imponible', type: 'number' },
                    { key: 'iva', label: 'IVA', type: 'number' },
                    { key: 'recargo_equivalencia', label: 'Recargo Equiv.', type: 'number' },
                    { key: 'total_factura', label: 'Total Factura', type: 'number' },
                    { key: 'referencia_proveedor', label: 'Ref. Proveedor' },
                    { key: 'departamento', label: 'Departamento', type: 'select' },
                  ]?.map(field => (
                    <div key={field?.key}>
                      <label className="font-oranienbaum text-white/40 text-xs block mb-1">{field?.label}</label>
                      {field?.type === 'select' ? (
                        <select value={ocrData?.[field?.key]} onChange={e => updateOCR(field?.key, e?.target?.value)}
                          className="w-full font-oranienbaum text-sm rounded-lg px-3 py-2 border outline-none text-white"
                          style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)' }}>
                          <option value="">Seleccionar...</option>
                          {DEPARTMENTS?.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      ) : (
                        <input type={field?.type || 'text'} value={ocrData?.[field?.key]} onChange={e => updateOCR(field?.key, e?.target?.value)}
                          className="w-full font-oranienbaum text-sm rounded-lg px-3 py-2 border outline-none text-white placeholder-white/20"
                          style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)' }}
                          placeholder={`Introduce ${field?.label?.toLowerCase()}`} />
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <label className="font-oranienbaum text-white/40 text-xs block mb-1">Observaciones</label>
                  <textarea value={ocrData?.observaciones} onChange={e => updateOCR('observaciones', e?.target?.value)} rows={2}
                    className="w-full font-oranienbaum text-sm rounded-lg px-3 py-2 border outline-none text-white placeholder-white/20 resize-none"
                    style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.12)' }} placeholder="Observaciones adicionales..." />
                </div>
              </div>

              {/* Line items */}
              <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center justify-between">
                  <h4 className="font-oranienbaum font-bold text-white/70 text-sm uppercase tracking-widest">Líneas de Producto</h4>
                  <button onClick={addLine} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                    style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}30` }}>
                    <Icon name="Plus" size={12} /> Añadir línea
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs font-oranienbaum">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        {['Producto', 'Cantidad', 'Precio Compra', 'Descuento %', 'IVA %', 'Total Línea', '']?.map(h => (
                          <th key={h} className="text-left py-2 px-2 text-white/40 font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lines?.map((line, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          {['producto', 'cantidad', 'precio_compra', 'descuento', 'iva', 'total_linea']?.map(field => (
                            <td key={field} className="py-1.5 px-1">
                              <input type={['cantidad', 'precio_compra', 'descuento', 'iva', 'total_linea']?.includes(field) ? 'number' : 'text'}
                                value={line?.[field]} onChange={e => updateLine(idx, field, e?.target?.value)}
                                className="w-full font-oranienbaum text-xs rounded-lg px-2 py-1.5 border outline-none text-white placeholder-white/20"
                                style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.10)', minWidth: field === 'producto' ? '120px' : '70px' }}
                                placeholder="—" />
                            </td>
                          ))}
                          <td className="py-1.5 px-1">
                            <button onClick={() => removeLine(idx)} className="w-6 h-6 rounded flex items-center justify-center text-white/25 hover:text-red-400 transition-colors">
                              <Icon name="Trash2" size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between gap-3 pt-2">
                <button onClick={reset} className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-oranienbaum text-sm text-white/50 hover:text-white border border-white/10 hover:border-white/20 transition-all">
                  <Icon name="RotateCcw" size={14} /> Nueva captura
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={onClose} className="px-4 py-2.5 rounded-xl font-oranienbaum text-sm text-white/50 hover:text-white border border-white/10 transition-all">
                    Cancelar
                  </button>
                  <button onClick={handleSave}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-oranienbaum font-bold text-sm transition-all hover:opacity-90"
                    style={{ background: accent, color: '#000' }}>
                    <Icon name="Save" size={14} /> Guardar y Validar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP: SAVED ── */}
          {step === 'saved' && (
            <div className="flex flex-col items-center justify-center py-12 gap-5">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}>
                <Icon name="CheckCircle" size={32} style={{ color: accent }} />
              </div>
              <div className="text-center">
                <h3 className="font-oranienbaum font-bold text-white text-xl mb-2">Documento guardado correctamente</h3>
                <p className="font-oranienbaum text-white/40 text-sm">El documento ha sido procesado, validado y archivado en el sistema</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={reset}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-oranienbaum font-bold text-sm transition-all"
                  style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}30` }}>
                  <Icon name="Camera" size={14} /> Capturar otro
                </button>
                <button onClick={onClose}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-oranienbaum font-bold text-sm transition-all"
                  style={{ background: accent, color: '#000' }}>
                  <Icon name="Check" size={14} /> Finalizar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
